import { ReactElement } from 'react';
import RichText from 'components/UI/Richtext';
import { IComponentTextWithIconsAboveFields } from 'types/contentful';
import { Asset } from 'contentful';
import Image from 'components/UI/Image';
import CTA from 'components/UI/Cta';
import { LINK_CONTENT_TYPE } from 'lib/consts';
import IFrameCTA from 'components/UI/IFrameCta';

const ICON_SIZE_CONFIG = {
  Small: { wrapper: 'w-16', query: 'w=64' },
  Medium: { wrapper: 'w-24', query: 'w=96' },
  Large: { wrapper: 'w-32', query: 'w=128' },
  XLarge: { wrapper: 'w-48', query: 'w=160' },
} as const;

export default function TextWithIconsAbove({
  heading,
  body,
  icons,
  iconSize = 'Medium',
  ctas,
  backgroundColor,
}: IComponentTextWithIconsAboveFields): ReactElement {
  const { wrapper, query } =
    ICON_SIZE_CONFIG[iconSize as keyof typeof ICON_SIZE_CONFIG] ?? ICON_SIZE_CONFIG.Medium;
  return (
    <div className={`bg-${backgroundColor} ecp-my`} id="vision-testing">
      <div
        className={`text-primary-actual mx-auto ${
          backgroundColor ? 'py-10 md:py-20' : 'pb-10 md:pb-20'
        }  px-site-component`}
      >
        {/* Icons moved above the rich text section */}
        {icons && icons.length > 0 && (
          <div className="flex flex-row justify-center w-full mb-2 space-x-10">
            {icons.map((icon, index) => (
              <div key={index} className={`mx-1 ${wrapper}`}>
                <Image
                  image={icon as Asset}
                  query={query}
                  classNames="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        {heading && (
          <h2 className="font-light text-new-34 lg:text-4xl lg:leading-new-43 text-center">
            {heading}
          </h2>
        )}

        <div className="mt-4">
          {body && (
            <div className={`mt-1 text-lg`}>
              <RichText document={body} classNames="text-lg text-primary-actual" />
            </div>
          )}

          {/* CTAs rendered where icons were previously positioned */}
          {ctas && ctas.length > 0 && (
            <div className="mt-8 flex flex-col items-center justify-center w-full space-y-4 md:flex-row md:space-y-0 md:space-x-6">
              {ctas.map((cta, index) => {
                const linkContentType = cta.sys.contentType.sys.id;
                if (linkContentType === LINK_CONTENT_TYPE.fragmentIFrameCta) {
                  return (
                    <div key={cta.sys.id ?? index}>
                      <IFrameCTA key={index} {...cta.fields} backgroundColor="black" />
                    </div>
                  );
                }
                return (
                  <div key={cta.sys.id ?? index}>
                    <CTA {...cta.fields} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
